function initDraggableSliders(selector = '.js-draggable-slider') {
  const sliders = document.querySelectorAll(selector);

  sliders.forEach(slider => {
    const DRAG_THRESHOLD = 8; // px
    let isDown = false;
    let isDragging = false;
    let wasDragging = false;
    let startX, startY, startScrollLeft;
    let velocity = 0;
    let lastX = 0;
    let animationId = null;

    // Avoid text/image dragging, but don't kill normal clicks.
    slider.style.userSelect = 'none';
    slider.addEventListener('dragstart', (e) => e.preventDefault());

    const findNearestSnapPoint = () => {
      const children = Array.from(slider.children);
      if (!children.length) return slider.scrollLeft;

      const sliderWidth = slider.clientWidth;
      const current = slider.scrollLeft;

      const first = children[0];
      const columnWidth = first.offsetWidth;
      const gap = children[1]
        ? children[1].offsetLeft - children[0].offsetLeft - columnWidth
        : 0;

      const maxScroll = slider.scrollWidth - sliderWidth;
      const snaps = [];
      for (let i = 0; i < children.length; i++) {
        const pos = i * (columnWidth + gap);
        if (pos <= maxScroll) snaps.push(pos);
      }
      if (snaps.length && snaps[snaps.length - 1] < maxScroll) snaps.push(maxScroll);

      let closest = current, best = Infinity;
      snaps.forEach(p => {
        const d = Math.abs(current - p);
        if (d < best) { best = d; closest = p; }
      });
      return closest;
    };

    const smoothScrollTo = (target) => {
      const start = slider.scrollLeft;
      const dist = target - start;
      const duration = 300;
      const t0 = performance.now();
      const tick = (t) => {
        const p = Math.min((t - t0) / duration, 1);
        const easeOut = 1 - Math.pow(1 - p, 3);
        slider.scrollLeft = start + dist * easeOut;
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    const momentumScroll = () => {
      if (Math.abs(velocity) > 0.5) {
        slider.scrollLeft += velocity;
        velocity *= 0.92;
        animationId = requestAnimationFrame(momentumScroll);
      } else {
        velocity = 0;
        if (animationId) cancelAnimationFrame(animationId);
        animationId = null;
        smoothScrollTo(findNearestSnapPoint());
      }
    };

    slider.addEventListener('mousedown', (e) => {
      // DO NOT preventDefault here—otherwise clicks won’t register.
      const rect = slider.getBoundingClientRect();
      isDown = true;
      isDragging = false;
      startX = e.clientX - rect.left;
      startY = e.clientY - rect.top;
      startScrollLeft = slider.scrollLeft;
      lastX = e.clientX;
      velocity = 0;
      if (animationId) {
        cancelAnimationFrame(animationId);
        animationId = null;
      }
    });

    const endDrag = () => {
      if (!isDown) return;
      isDown = false;
      slider.classList.remove('dragging');

      if (isDragging) {
        wasDragging = true; // used to cancel the next click
        if (Math.abs(velocity) > 2) {
          momentumScroll();
        } else {
          smoothScrollTo(findNearestSnapPoint());
        }
      }
      isDragging = false;
    };

    slider.addEventListener('mouseleave', endDrag);
    slider.addEventListener('mouseup', endDrag);

    slider.addEventListener('mousemove', (e) => {
      if (!isDown) return;

      const rect = slider.getBoundingClientRect();
      const curX = e.clientX;
      const dx = curX - (startX + rect.left);
      const dy = e.clientY - (startY + rect.top);

      // Only start a "drag" after the threshold.
      if (!isDragging && Math.hypot(dx, dy) > DRAG_THRESHOLD) {
        isDragging = true;
        slider.classList.add('dragging');
      }
      if (!isDragging) return;

      // Now that we're actually dragging, prevent text selection, etc.
      e.preventDefault();

      velocity = (lastX - curX) * 0.8;
      lastX = curX;

      // Drag horizontally
      slider.scrollLeft = startScrollLeft - dx * 1.5;
    });

    // Suppress the click that follows a drag so links don't fire accidentally.
    slider.addEventListener('click', (e) => {
      if (wasDragging) {
        e.preventDefault();
        e.stopPropagation();
        // Reset after click phase completes
        setTimeout(() => { wasDragging = false; }, 0);
      }
    }, true); // capture to beat inner handlers

    // Ensure we don't get CSS smooth scroll fighting with JS during drag
    slider.style.scrollBehavior = 'auto';
  });
}

// extend the draggable slider to support auto scrolling if data-speed is set
const initAutoScrollSliders = (selector = '.js-draggable-slider') => {
  console.log('initAutoScrollSliders');
  const sliders = document.querySelectorAll(selector);
  console.log('Found sliders:', sliders);

  sliders.forEach(slider => {
    const speed = slider.getAttribute('data-speed');
    if (!speed) return;

    const intervalMs = parseFloat(speed) * 1000;
    let autoScrollInterval = null;
    let isPaused = false;

    const getNextSnapPoint = () => {
      const children = Array.from(slider.children);
      if (!children.length) return slider.scrollLeft;

      const sliderWidth = slider.clientWidth;
      const current = slider.scrollLeft;

      const first = children[0];
      const columnWidth = first.offsetWidth;
      const gap = children[1]
        ? children[1].offsetLeft - children[0].offsetLeft - columnWidth
        : 0;

      const maxScroll = slider.scrollWidth - sliderWidth;
      const snaps = [];
      for (let i = 0; i < children.length; i++) {
        const pos = i * (columnWidth + gap);
        if (pos <= maxScroll) snaps.push(pos);
      }
      if (snaps.length && snaps[snaps.length - 1] < maxScroll) snaps.push(maxScroll);

      // Find next snap point
      for (let i = 0; i < snaps.length; i++) {
        if (snaps[i] > current + 1) return snaps[i];
      }
      // Loop back to start if at the end
      return snaps[0] || 0;
    };

    const smoothScrollTo = (target) => {
      const start = slider.scrollLeft;
      const dist = target - start;
      const duration = 300;
      const t0 = performance.now();
      const tick = (t) => {
        const p = Math.min((t - t0) / duration, 1);
        const easeOut = 1 - Math.pow(1 - p, 3);
        slider.scrollLeft = start + dist * easeOut;
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    const autoScroll = () => {
      if (!isPaused) {
        const nextSnap = getNextSnapPoint();
        smoothScrollTo(nextSnap);
      }
    };

    // Start auto-scrolling
    autoScrollInterval = setInterval(autoScroll, intervalMs);

    // Pause on user interaction
    slider.addEventListener('mousedown', () => {
      isPaused = true;
    });

    slider.addEventListener('mouseup', () => {
      // Resume after a delay
      setTimeout(() => {
        isPaused = false;
      }, intervalMs);
    });

    // Pause on hover (optional, remove if not desired)
    slider.addEventListener('mouseenter', () => {
      isPaused = true;
    });

    slider.addEventListener('mouseleave', () => {
      isPaused = false;
    });

    // Store interval ID for potential cleanup
    slider._autoScrollInterval = autoScrollInterval;
  });
};

// Infinite slider.
function initInfiniteSliders(selector = '.js-infinite-slider') {
  const sliders = document.querySelectorAll(selector);
  
  sliders.forEach(slider => {
    const track = slider.querySelector('.slider-track');
    
    if (slider && track) {
      // Calculate optimal speed based on content width
      const trackWidth = track.scrollWidth / 2; // Divide by 2 since we duplicated content
      const viewportWidth = slider.offsetWidth;
      
      // Adjust animation duration based on content amount
      const baseSpeed = slider.getAttribute('data-speed') || 30; // Base speed in seconds
      const adjustedSpeed = Math.max(baseSpeed * (trackWidth / 1000), 15); // Minimum 15s
      
      track.style.animationDuration = adjustedSpeed + 's';
    }
    
    // Optional: Add pause/resume functionality based on the parent section's focus state
    const sliderSection = slider.parentElement;
    
    if (sliderSection) {
      // Pause on focus for accessibility
      sliderSection.addEventListener('focusin', function() {
        //track.style.animationPlayState = 'paused';
      });
      
      sliderSection.addEventListener('focusout', function() {
        //track.style.animationPlayState = 'running';
      });
    }
  });
};

document.addEventListener('DOMContentLoaded', () => {
  initDraggableSliders();
  initAutoScrollSliders();
  initInfiniteSliders();
});